import {
  CommunityItem,
  Event,
  Geogroup,
  HighlightedEventCard,
  HttpService,
  SearchIcon,
  UserGeogroup,
} from '@geovelo-frontends/commons';
import { Add } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import booleanIntersects from '@turf/boolean-intersects';
import { Link, navigate } from 'gatsby';
import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from '../../components/button';
import Dialog from '../../components/dialog';
import { AppContext } from '../../context';
import environment from '../../environment';

import CompanyMigrationDialog from './company-migration-dialog';

function UserCommunities(): React.JSX.Element {
  const [geogroups, setGeogroups] = useState<UserGeogroup[]>();
  const [companyMigrationDialogOpen, openCompanyMigrationDialog] = useState(false);
  const [companyToMigrate, setCompanyToMigrate] = useState<Geogroup>();
  const [highlightedEvent, setHighlightedEvent] = useState<Event | null>();
  const {
    app: { highlightedEvents },
    user: { current: currentUser, geogroups: userGeogroups, geolocation: userGeolocation },
  } = useContext(AppContext);
  const {
    t,
    i18n: { language: currentLanguage },
  } = useTranslation();
  const { transitions } = useTheme();

  useEffect(() => {
    setGeogroups(
      userGeogroups
        ?.filter(({ status }) => status === 'manuallyJoin')
        .sort((a, b) => {
          if (a.group.type === b.group.type) {
            return a.group.title.toLowerCase().localeCompare(b.group.title.toLowerCase());
          }

          if (a.group.type === 'company' && a.group.partner) return -1;
          if (b.group.type === 'company' && b.group.partner) return 1;
          if (a.group.type === 'city') return 1;
          if (b.group.type === 'city') return -1;

          return 0;
        }),
    );
  }, [userGeogroups]);

  useEffect(() => {
    if (!highlightedEvents) setHighlightedEvent(highlightedEvents);
    else if (!userGeolocation) setHighlightedEvent(highlightedEvents[0]);
    else
      setHighlightedEvent(
        highlightedEvents.find(
          ({ polygon }) => polygon && booleanIntersects(polygon, userGeolocation.point),
        ) || highlightedEvents[0],
      );
  }, [highlightedEvents, userGeolocation]);

  return (
    <>
      {geogroups && userGeolocation && (
        <HighlightedEventCard
          Button={Button}
          geogroup={null}
          hasGeogroups={geogroups.length > 0}
          highlightedEvent={highlightedEvent}
          highlightedEventChallenge={null}
        />
      )}
      <Box display="flex" flexDirection="column" gap={2}>
        <Box
          alignItems={{ xs: 'flex-start', md: 'center' }}
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
          justifyContent="space-between"
          minHeight={40}
        >
          <Typography fontSize="1.5rem" fontWeight={700}>
            {t('commons.communities.joined_communities.title')}
          </Typography>
          {geogroups && geogroups.length > 0 && (
            <Box alignItems="center" columnGap={2} display="flex" flexWrap="wrap" rowGap={1}>
              <Button
                color="primary"
                component={Link}
                size="medium"
                startIcon={<SearchIcon />}
                to={`/${currentLanguage}/communities/search`}
                variant="outlined"
              >
                {t('commons.actions.search')}
              </Button>
              <Button
                color="primary"
                component={Link}
                size="medium"
                startIcon={<Add />}
                to={`/${currentLanguage}/communities/create`}
                variant="contained"
              >
                {t('commons.communities.form.actions.create')}
              </Button>
            </Box>
          )}
        </Box>
        {!geogroups ? (
          <Box alignItems="stretch" display="flex" flexWrap="wrap" gap={3}>
            {[1, 2, 3, 4, 5].map((key) => (
              <CommunityItem key={key} />
            ))}
          </Box>
        ) : (
          <>
            {geogroups.length > 0 ? (
              <Box alignItems="stretch" display="flex" flexWrap="wrap" gap={3}>
                {geogroups.map(({ group: geogroup }) => (
                  <CommunityItem
                    joined
                    Button={Button}
                    currentUser={currentUser}
                    Dialog={Dialog}
                    geogroup={geogroup}
                    handleClick={() => {
                      if (geogroup.type === 'company') {
                        if (geogroup.partner) {
                          window.open(
                            `${environment.enterpriseUrl}?auth-token=${
                              HttpService.authorizationToken?.substring(
                                HttpService.authorizationToken.indexOf(' ') + 1,
                              ) || ''
                            }&user-id=${currentUser?.id || ''}&partner-id=${geogroup.partner.id}`,
                            '_blank',
                          );
                        } else {
                          setCompanyToMigrate(geogroup);
                          openCompanyMigrationDialog(true);
                        }
                      } else {
                        navigate(`/${currentLanguage}/communities/${geogroup.id}`);
                      }
                    }}
                    key={geogroup.id}
                  />
                ))}
              </Box>
            ) : (
              <Box
                alignItems="center"
                border="1px solid rgba(0, 0, 0, 0.12)"
                borderRadius={2}
                display="flex"
                flexDirection="column"
                gap={3}
                justifyContent="center"
                padding={5}
                width="100%"
              >
                <Typography>{t('geovelo.communities.empty_state')}</Typography>
                <Box alignItems="center" columnGap={2} display="flex" flexWrap="wrap" rowGap={1}>
                  <Button
                    color="primary"
                    component={Link}
                    size="small"
                    startIcon={<SearchIcon />}
                    to={`/${currentLanguage}/communities/search`}
                    variant="outlined"
                  >
                    {t('commons.communities.actions.search')}
                  </Button>
                  <Button
                    color="primary"
                    component={Link}
                    size="medium"
                    startIcon={<Add />}
                    to={`/${currentLanguage}/communities/create`}
                    variant="contained"
                  >
                    {t('commons.communities.form.actions.create')}
                  </Button>
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>
      <CompanyMigrationDialog
        geogroup={companyToMigrate}
        onClose={() => {
          openCompanyMigrationDialog(false);
          setTimeout(() => setCompanyToMigrate(undefined), transitions.duration.leavingScreen);
        }}
        open={companyMigrationDialogOpen}
      />
    </>
  );
}

export default UserCommunities;
